// The most calls one test-user wipe makes before reporting it unfinished (as the CLI).
const WIPE_MAX_CALLS = 200;

export default {
  async registerThirdParty(
    appName,
    description,
    redirectUris,
    developerEmail,
    websiteUrl,
    _dataLenderRequest,
    {
      yaml,
      deployment_mode,
      app_store_visibility,
      allowed_user_ids,
      group_id,
      category
    } = {}
  ) {
    // Validate app name
    if (!appName || appName.trim() === '') {
      throw new Error('App name is required');
    }

    // Get current user's email for developer_email
    const user = await this.getMe();

    const requestBody = {
      name: appName.trim(),  // Backend expects 'name', not 'app_name'
      developer_email: (developerEmail && developerEmail.trim()) || user.email  // Required by backend
    };

    // Only add optional fields if they have values
    if (description && description.trim()) {
      requestBody.description = description.trim();
    }
    if (websiteUrl && websiteUrl.trim()) {
      requestBody.website_url = websiteUrl.trim();
    }
    if (redirectUris && redirectUris.length > 0) {
      const validUris = redirectUris.filter(u => u && u.trim());
      if (validUris.length > 0) {
        requestBody.redirect_uris = validUris;
      }
    }
    // YAML is the source of truth for all DLR permission fields
    if (yaml) {
      requestBody.yaml = yaml;
    }

    if (deployment_mode) {
      requestBody.deployment_mode = deployment_mode;
    }
    if (app_store_visibility) {
      requestBody.app_store_visibility = app_store_visibility;
    }
    if (Array.isArray(allowed_user_ids)) {
      requestBody.allowed_user_ids = allowed_user_ids;
    }
    if (group_id) {
      requestBody.group_id = group_id;
    }
    if (category) {
      requestBody.category = category;
    }
    return this.request('/third-party/register', {
      method: 'POST',
      body: requestBody,
    });
  },

  async updateThirdParty(appId, updates) {
    return this.request(`/third-party/${appId}`, {
      method: 'PUT',
      body: updates,
    });
  },

  async getAppVersions(appId) {
    return this.request(`/third-party/${appId}/versions`);
  },

  async rollbackApp(appId, version, epAction) {
    const body = { version };
    if (epAction) body.ep_action = epAction;
    return this.request(`/third-party/${appId}/rollback`, {
      method: 'POST',
      body,
    });
  },

  async getTestUsers(appId) {
    return this.request(`/third-party/${appId}/test-users`);
  },

  async createTestUser(appId) {
    return this.request(`/third-party/${appId}/test-users`, { method: 'POST' });
  },

  // A large sandbox is wiped over several calls: each answers 202 `done: false`
  // with what is left, until the last. These keep calling, and `deleted` is the
  // sum across calls — the last call alone is only the final chunk. A reply
  // with `done: false` here means the wipe stopped unfinished.
  async resetTestUser(appId, userId) {
    return this._untilWiped(`/third-party/${appId}/test-users/${userId}/reset`, 'POST');
  },

  async deleteTestUser(appId, userId) {
    return this._untilWiped(`/third-party/${appId}/test-users/${userId}`, 'DELETE');
  },

  async _untilWiped(path, method) {
    const deleted = { nodes: 0, relationships: 0 };
    let res = {};
    for (let i = 0; i < WIPE_MAX_CALLS; i += 1) {
      res = (await this.request(path, { method })) || {};
      deleted.nodes += res.deleted?.nodes || 0;
      deleted.relationships += res.deleted?.relationships || 0;
      // A backend without `done` finished in one call.
      if (res.done !== false) return { ...res, deleted, done: true };
    }
    return { ...res, deleted, done: false };
  },

  // Sandbox GROUPS. A test user exercises the personal paths; a group agent,
  // a group realm and a group EP can only be reached through a group, and
  // borrowing a real one is what makes a run unrecordable.
  async getTestGroups(appId) {
    return this.request(`/third-party/${appId}/test-groups`);
  },

  async createTestGroup(appId, adminUserId) {
    return this.request(`/third-party/${appId}/test-groups`, {
      method: 'POST',
      body: adminUserId ? { admin_user_id: adminUserId } : {},
    });
  },

  async addTestGroupMember(appId, groupId, userId) {
    return this.request(`/third-party/${appId}/test-groups/${groupId}/members`, {
      method: 'POST',
      body: { user_id: userId },
    });
  },

  async setTestUserPassword(appId, userId, password) {
    return this.request(`/third-party/${appId}/test-users/${userId}/password`, {
      method: 'PUT',
      body: { password },
    });
  },

  async setTestUserLabel(appId, userId, label) {
    return this.request(`/third-party/${appId}/test-users/${userId}/label`, {
      method: 'PUT',
      body: { label },
    });
  },

  async setTestGroupLabel(appId, groupId, label) {
    return this.request(`/third-party/${appId}/test-groups/${groupId}/label`, {
      method: 'PUT',
      body: { label },
    });
  },

  async deleteTestGroup(appId, groupId) {
    return this.request(`/third-party/${appId}/test-groups/${groupId}`, { method: 'DELETE' });
  },

  // The app's own published node graph — the catalog a user's own node can
  // later link to (see backend/app/authz/app_catalog_access.py). Dev-portal
  // only; there is no end-user-facing surface for this.
  async getAppNodes(appId, { schemaName, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (schemaName) params.set('schema_name', schemaName);
    if (limit != null) params.set('limit', limit);
    if (offset != null) params.set('offset', offset);
    const query = params.toString() ? `?${params}` : '';
    return this.request(`/third-party/${appId}/nodes${query}`);
  },

  async createAppNode(appId, { title, schemaName, valueJson }) {
    const body = { title, value_json: valueJson || {} };
    if (schemaName) body.schema_name = schemaName;
    return this.request(`/third-party/${appId}/nodes`, { method: 'POST', body });
  },

  async deleteAppNode(appId, nodeId) {
    return this.request(`/third-party/${appId}/nodes/${nodeId}`, { method: 'DELETE' });
  },

  // The app's keyring — one credential per machine that runs it. The LIST never
  // carries a key: they are stored hashed and cannot be read back, which is why
  // a developer who does not have one mints their own rather than asking for it.
  async getAppKeys(appId, includeRevoked = false) {
    const q = includeRevoked ? '?include_revoked=1' : '';
    return this.request(`/third-party/${appId}/keys${q}`);
  },

  // The ONLY sight of the key. Returned once, never retrievable afterwards.
  async createAppKey(appId, name) {
    return this.request(`/third-party/${appId}/keys`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async revokeAppKey(appId, keyId) {
    return this.request(`/third-party/${appId}/keys/${keyId}`, { method: 'DELETE' });
  },

  async getAuthorizedUsers(appId, page = 1) {
    return this.request(`/third-party/${appId}/authorized-users?page=${page}`);
  },

  async getChangelog() {
    return this.request('/docs/changelog');
  },

  /**
   * Validate and RENDER a pricing blob without storing it.
   *
   * The editor's live preview goes through here rather than formatting locally: one
   * renderer, so the editor, the consent card and the store badge cannot disagree about
   * what a price says. Returns {ok, error, pricing_display} — and renders a
   * "Price unavailable" card for an invalid blob, which is what a developer should see
   * for a price in that state.
   */
  async previewPricing(pricing) {
    return this.request('/third-party/pricing/preview', {
      method: 'POST',
      body: { pricing },
    });
  },

  /** Stage any spec field (pricing included) into the app's one pending change.
   *
   *  PATCH, not POST. /draft registers GET, PATCH and DELETE — POST exists only on
   *  /draft/commit — so this answered 405 and the Pricing tab's Stage button did
   *  nothing. The Access, CRUX and details editors already stage with PATCH; they
   *  and this now write into the same pending change. */
  async stageDraft(appId, fields) {
    return this.request(`/third-party/${appId}/draft`, {
      method: 'PATCH',
      body: fields,
    });
  },
};
